import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let tokenGetter = null;
let sessionExpired = null;
export const setTokenGetter = (fn) => { tokenGetter = fn; };
export const setSessionExpiredHandler = (fn) => { sessionExpired = fn; };
export const getAuthToken = async (opts) => (tokenGetter ? tokenGetter(opts) : null);

const COMPANY_KEY = "sdr_company";
let companyId = localStorage.getItem(COMPANY_KEY);
export const getCompanyId = () => companyId;
export const setCompanyId = (id) => { companyId = id; if (id) localStorage.setItem(COMPANY_KEY, id); };

const SESSION_ERRORS = ["Invalid or expired session", "Not signed in"];

export const api = axios.create({ baseURL: API });
api.interceptors.request.use(async (config) => {
  const t = await getAuthToken(config._fresh ? { skipCache: true } : undefined);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  if (companyId) config.headers["X-Company-Id"] = companyId;
  return config;
});
api.interceptors.response.use(null, async (error) => {
  const cfg = error.config;
  if (error.response?.status === 401 && SESSION_ERRORS.includes(error.response.data?.detail) && cfg) {
    if (!cfg._fresh) {
      cfg._fresh = true;
      try { return await api.request(cfg); } catch (e) { error = e; }
    }
    if (error.response?.status === 401 && SESSION_ERRORS.includes(error.response.data?.detail)) sessionExpired?.();
  }
  return Promise.reject(error);
});

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (SESSION_ERRORS.includes(d)) return "Votre session a expiré, merci de vous reconnecter.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return "Une erreur est survenue";
};

export const fmtBytes = (n = 0) => {
  if (!n) return "0 o";
  const u = ["o", "Ko", "Mo", "Go", "To"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / 1024 ** i).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} ${u[i]}`;
};

export const fmtDate = (s, withTime = false) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString("fr-FR", withTime
    ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "numeric" });
};

export const fmtMoney = (v, cur = "EUR") =>
  v === null || v === undefined || v === "" ? "—"
    : Number(v).toLocaleString("fr-FR", { style: "currency", currency: cur || "EUR" });

export const ROLE_LABELS = { admin: "Administrateur", responsable: "Responsable", salarie: "Salarié", externe: "Externe" };

export const CATEGORY_LABELS = {
  facture: "Facture", devis: "Devis", contrat: "Contrat", bon_de_livraison: "Bon de livraison",
  fiche_technique: "Fiche technique", rh: "RH", administratif: "Administratif", autre: "Autre",
};

export const ACTION_LABELS = {
  connexion: "Connexion", creation_dossier: "Création de dossier", renommage_dossier: "Renommage de dossier",
  deplacement_dossier: "Déplacement de dossier", suppression_dossier: "Suppression de dossier",
  televersement: "Téléversement", nouvelle_version: "Nouvelle version", consultation: "Consultation",
  telechargement: "Téléchargement", renommage: "Renommage", deplacement: "Déplacement", modification: "Modification",
  copie: "Copie", restauration_version: "Restauration de version", suppression: "Mise à la corbeille",
  restauration: "Restauration", suppression_definitive: "Suppression définitive",
  modification_autorisations: "Modification des autorisations", commentaire: "Commentaire",
  creation_lien_partage: "Création de lien", revocation_lien: "Révocation de lien", acces_lien_public: "Accès lien public",
  analyse_ia: "Analyse IA", classement_auto: "Classement automatique", question_ia: "Question à l'IA",
  changement_role: "Changement de rôle", activation_compte: "Activation de compte",
  desactivation_compte: "Désactivation de compte", invitation_utilisateur: "Invitation", export_complet: "Export intégral",
  creation_acces_lecteur: "Accès synchronisation créé", revocation_acces_lecteur: "Accès synchronisation révoqué",
  lecture_notice_rgpd: "Lecture notice RGPD", parametres_rgpd: "Paramètres RGPD",
  export_donnees_personnelles: "Export de données personnelles", effacement_utilisateur: "Effacement d'utilisateur",
  telechargement_export: "Téléchargement de l'export",
};

export const fileKind = (f) => {
  const ext = (f?.ext || "").toLowerCase();
  const ct = f?.content_type || "";
  if (ext === "pdf" || ct === "application/pdf") return "pdf";
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("video/")) return "video";
  if (ct.startsWith("audio/")) return "audio";
  if (["doc", "docx", "odt", "rtf"].includes(ext)) return "word";
  if (["xls", "xlsx", "xlsm", "ods", "csv"].includes(ext)) return "sheet";
  if (["ppt", "pptx", "odp"].includes(ext)) return "slides";
  if (["txt", "md", "json", "xml", "html", "log"].includes(ext) || ct.startsWith("text/")) return "text";
  if (["zip", "rar", "7z"].includes(ext)) return "archive";
  return "other";
};

export const saveBlob = (blob, name) => {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 2000);
};

export const publicLinkUrl = (token) => `${window.location.origin}/s/${token}`;
